import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { useActiveShow } from "common-fe";
import { useStationMonitor } from "../hooks/useStationMonitor";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { groupStationRowsByScribe } from "../util/stationMonitor";
import { formatElapsedLabel } from "../util/scribePresence";
import { formatVenueLocalDateTime } from "../util/classDisplay";

// A plain ticking clock, same pattern as ScribePresenceSection.jsx's useNow:
// a re-render trigger so elapsed-time cells advance on screen between
// fetches rather than freezing at whatever value was true on load.
function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

const CLOCK_TICK_MS = 5000;
// Placeholder glyph for an empty cell (U+2014), escaped so source stays dash-free.
const EMPTY_CELL = "\u2014";

function elapsedFrom(iso, now, t) {
  if (!iso) return t("stationMonitor.never");
  return t("stationMonitor.lastSeen", { time: formatElapsedLabel(Math.max(0, now - new Date(iso).getTime())) });
}

// #2827 (scribe-resilience workstream, docs/score/scribe-resilience.md gap
// G12 part B) — the scoreadmin mirror of scribe-fe's own outbox (C18): reads
// every station's latest uploaded snapshot (#2929's intake, #2827's read
// side) plus live presence, grouped by scribe (AC1), so the tent can see the
// state of every ring without walking to it. Row-building/grouping logic is
// entirely in util/stationMonitor.js (unit tested there) — this component is
// render only.
export default function StationMonitor() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.stationMonitor"));
  const { activeShowId } = useActiveShow();
  const { rows, loading, error, reconcileStation, markReviewed, refetch } = useStationMonitor(activeShowId);
  const now = useNow(CLOCK_TICK_MS);

  // #2828 (scribe-resilience workstream, G12 part B AC1-AC3) — per-row busy/
  // error state for the reconcile/mark-reviewed actions below. Keyed by
  // row.key (same key the table already uses), never global: two rows can be
  // in flight independently, and one row's error must never blank another
  // row's button. Plain local state, not the hook's own state, because it's
  // page-local UI feedback, not data the hook's refetch() would ever restore.
  const [rowState, setRowState] = useState({});
  const patchRow = (key, patch) =>
    setRowState((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const handleReconcile = async (row) => {
    patchRow(row.key, { busy: true, error: null });
    try {
      await reconcileStation(row.deviceId, row.scribeSub);
      patchRow(row.key, { busy: false, error: null });
    } catch {
      patchRow(row.key, { busy: false, error: t("stationMonitor.reconcile.reconcileError") });
    }
  };

  const handleMarkReviewed = async (row) => {
    patchRow(row.key, { busy: true, error: null });
    try {
      await markReviewed(row.deviceId, row.scribeSub, row.reconcile.reconciledAt);
      patchRow(row.key, { busy: false, error: null });
    } catch (err) {
      // #2828 judge-early-1 fix 4 — a 409 here means a FRESH reconcile landed
      // server-side since the operator loaded this row's reconciledAt (the
      // optimistic lock this whole flow exists to protect). Distinguish it
      // (same err?.response?.status === 409 check RideEditor.jsx already
      // uses for its own concurrent-write 409) from any other failure, and
      // say so in plain language rather than a generic error — the operator's
      // own next reconcile/mark-reviewed click will pick up the row this
      // component's own refetch (via the hook) already brought back.
      const isConflict = err?.response?.status === 409;
      patchRow(row.key, {
        busy: false,
        error: t(isConflict ? "stationMonitor.reconcile.staleReconcile" : "stationMonitor.reconcile.markReviewedError"),
      });
      // A 409 means THIS row's reconciledAt is stale; refetch so the row the
      // operator sees (and any next click reads reconciledAt from) is
      // current, rather than repeating the same 409 forever.
      if (isConflict) refetch();
    }
  };

  const groups = groupStationRowsByScribe(rows);

  return (
    <Box>
      <Typography variant="h5" gutterBottom color="text.primary">
        {t("stationMonitor.title")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="station-monitor-load-error">
          {t("stationMonitor.loadError")}
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : rows.length === 0 ? (
            <Typography color="text.secondary" data-testid="station-monitor-empty">
              {t("stationMonitor.noStations")}
            </Typography>
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" data-testid="station-monitor-table">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("stationMonitor.columns.scribe")}</TableCell>
                    <TableCell>{t("stationMonitor.columns.station")}</TableCell>
                    <TableCell>{t("stationMonitor.columns.ringClasses")}</TableCell>
                    <TableCell align="right">{t("stationMonitor.columns.shipped")}</TableCell>
                    <TableCell align="right">{t("stationMonitor.columns.queued")}</TableCell>
                    <TableCell align="right">{t("stationMonitor.columns.needsAttention")}</TableCell>
                    <TableCell>{t("stationMonitor.columns.oldestUnshipped")}</TableCell>
                    <TableCell>{t("stationMonitor.columns.reconcile")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((group) => (
                    <GroupRows
                      key={group.scribeSub || "unknown"}
                      group={group}
                      now={now}
                      t={t}
                      rowState={rowState}
                      onReconcile={handleReconcile}
                      onMarkReviewed={handleMarkReviewed}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function GroupRows({ group, now, t, rowState, onReconcile, onMarkReviewed }) {
  return group.rows.map((row) => (
    <TableRow key={row.key} data-testid={`station-monitor-row-${row.key}`}>
      <TableCell>
        <Typography variant="body2" color="text.primary">
          {group.scribeName || group.scribeSub || t("common.unknownUser")}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.primary">
          {row.deviceId}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {elapsedFrom(row.lastSeenAt, now, t)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {row.ring ?? EMPTY_CELL}
        </Typography>
      </TableCell>
      {row.neverUploaded ? (
        <TableCell colSpan={5} data-testid={`station-monitor-never-uploaded-${row.key}`}>
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={
              // #2827 judge fix r1 (FE item 2): a device that changed hands
              // mid-show has snapshot history, just not under THIS scribe —
              // say so, rather than implying the station itself is silent.
              row.deviceSeenElsewhere
                ? t("stationMonitor.noSnapshotForScribe")
                : t("stationMonitor.neverUploaded")
            }
          />
        </TableCell>
      ) : (
        <>
          <TableCell align="right" data-testid={`station-monitor-shipped-${row.key}`}>
            {row.counts.shipped}
          </TableCell>
          <TableCell align="right" data-testid={`station-monitor-queued-${row.key}`}>
            {row.counts.queued}
          </TableCell>
          <TableCell align="right" data-testid={`station-monitor-needs-attention-${row.key}`}>
            {row.counts.needsAttention > 0 ? (
              <Chip size="small" color="error" label={row.counts.needsAttention} />
            ) : (
              row.counts.needsAttention
            )}
          </TableCell>
          <TableCell data-testid={`station-monitor-oldest-unshipped-${row.key}`}>
            {row.oldestUnshippedAt
              ? formatElapsedLabel(Math.max(0, now - new Date(row.oldestUnshippedAt).getTime()))
              : EMPTY_CELL}
          </TableCell>
          <TableCell data-testid={`station-monitor-reconcile-${row.key}`}>
            <ReconcileCell
              row={row}
              t={t}
              state={rowState[row.key]}
              onReconcile={onReconcile}
              onMarkReviewed={onMarkReviewed}
            />
          </TableCell>
        </>
      )}
    </TableRow>
  ));
}

// #2828 (scribe-resilience workstream, G12 part B AC1-AC3) — the reconcile/
// mark-reviewed controls for one row. Three states, driven entirely by
// row.reconcile (score-api's listStationSnapshots join, util/stationMonitor.js):
//   - never reconciled (row.reconcile === null): just a Reconcile button.
//   - reconciled, not yet reviewed: disagreement count + Mark reviewed +
//     Reconcile (re-run). Mark reviewed forwards THIS row's own
//     reconciledAt (never re-derived) — the optimistic lock judge-early-1
//     fix 4 depends on.
//   - reviewed: who/when, still with Reconcile (re-run) available.
function ReconcileCell({ row, t, state, onReconcile, onMarkReviewed }) {
  const busy = state?.busy ?? false;
  const reconcile = row.reconcile;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
      {reconcile && (
        <Typography variant="body2" color="text.primary" data-testid={`station-monitor-reconcile-status-${row.key}`}>
          {reconcile.reviewed
            ? t("stationMonitor.reconcile.reviewedBy", {
                by: reconcile.reviewedBy,
                time: formatVenueLocalDateTime(reconcile.reviewedAt),
              })
            : t("stationMonitor.reconcile.disagreementCount", { count: reconcile.disagreementCount ?? 0 })}
        </Typography>
      )}
      {!reconcile && (
        <Typography variant="caption" color="text.secondary">
          {t("stationMonitor.reconcile.neverReconciled")}
        </Typography>
      )}
      {state?.error && (
        <Typography
          variant="caption"
          color="error.main"
          data-testid={`station-monitor-reconcile-error-${row.key}`}
        >
          {state.error}
        </Typography>
      )}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          data-testid={`station-monitor-reconcile-button-${row.key}`}
          onClick={() => onReconcile(row)}
        >
          {t("stationMonitor.reconcile.reconcileButton")}
        </Button>
        {reconcile && !reconcile.reviewed && (
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            data-testid={`station-monitor-mark-reviewed-button-${row.key}`}
            onClick={() => onMarkReviewed(row)}
          >
            {t("stationMonitor.reconcile.markReviewedButton")}
          </Button>
        )}
      </Box>
    </Box>
  );
}
